import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { onAuthStateChanged, User, signOut } from "firebase/auth";
import { auth } from "../infrastructure/firebase";
import { AuthContext } from "./AuthContext";
import { useSessionTimeout } from "./useSessionTimeout";
import { useRecordingActivity } from "./useRecordingActivity";

// 1 hour in milliseconds
const TIMEOUT_MS = 60 * 60 * 1000;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState<string | null>(null);
  const queryClient = useQueryClient();
  // Whose data the query cache holds; undefined until the first answer.
  const cachedFor = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      // Most queries are keyed without the account they belong to (which
      // tools are adopted, the therapist's profile...), so a cache filled by
      // one account would answer for the next one signed in on the same tab.
      const uid = firebaseUser?.uid ?? null;
      if (cachedFor.current !== undefined && cachedFor.current !== uid) {
        queryClient.clear();
      }
      cachedFor.current = uid;

      if (firebaseUser) {
        // Check localStorage for session expiration right at startup
        const lastActivityStr = localStorage.getItem('lastActivity');
        if (lastActivityStr) {
          const lastActivity = parseInt(lastActivityStr, 10);
          if (Date.now() - lastActivity > TIMEOUT_MS) {
            await signOut(auth);
            localStorage.removeItem('lastActivity');
            setUser(null);
            setIdToken(null);
            setLoading(false);
            return;
          }
        }

        // Force-refresh when email is unverified so stale cached token is updated
        const forceRefresh = !firebaseUser.emailVerified;
        const token = await firebaseUser.getIdToken(forceRefresh);
        setUser(auth.currentUser ?? firebaseUser);
        setIdToken(token);
      } else {
        setUser(null);
        setIdToken(null);
      }

      setLoading(false);
    });

    return () => unsub();
  }, [queryClient]);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error("Logout error", e);
    }
    localStorage.removeItem('lastActivity');
    setUser(null);
    setIdToken(null);
  }, []);

  // Suspend inactivity-based auto-logout while a recording is in progress.
  // During a live session the user may sit silently for long stretches with
  // no mouse/keyboard input, which would otherwise trip the inactivity timer
  // and discard the in-memory recording. See RecordingActivityContext.
  const { isRecordingActive } = useRecordingActivity();

  useSessionTimeout({
    timeoutMs: TIMEOUT_MS,
    onTimeout: logout,
    enabled: Boolean(user) && !isRecordingActive,
  });

  return (
    <AuthContext.Provider value={{ user, loading, idToken, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
